// Screen 11 — CRM.
//
// Three tabs over the multi-tenant relationship records — Clients, Agencies
// (internal), BD partners — each a dense table, with a right-anchored detail
// Drawer that opens on a row click. Agencies carry a standing note that the
// agency-facing product is deferred: these are admin-internal records with no
// agency login.
//
// REAL SHAPES (admin Go handlers — source of truth):
//   GET /admin/clients  -> { clients:  { id, name, email, enabled, created_at }[] }
//   GET /admin/agencies -> { agencies: { id, name, geos, fee_pct, financial_conditions,
//                                        role_scope, status }[] }
//   GET /admin/bd       -> { bd_partners: { id, name, scope, status, profit_tiers }[] }
//   GET /admin/clients/{id}/balance -> { gross_topup, balance, total_credit, total_debit }
// There is NO denormalized CRM-client endpoint, so the per-client top-up/net
// figures are fetched from /balance per row (tolerant — a slow/failed balance
// degrades to "—", never blanks the table).
//
// MONEY: top-up / net-budget figures are exact decimal strings rendered via
// MoneyCell.

import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import { TelegramChatBinding } from "@/components/TelegramChatBinding";
import {
  DataTable,
  Drawer,
  MoneyCell,
  StatusPill,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Segmented } from "./controls";

// --- Real backend shapes -----------------------------------------------------
interface ApiClient {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  created_at: number;
}
interface ApiAgency {
  id: string;
  name: string;
  geos: string[];
  fee_pct: string;
  financial_conditions: string;
  role_scope: string;
  status: string;
}
interface ApiBdTier {
  threshold_min: string;
  threshold_max: string;
  pct: string;
  note: string;
}
interface ApiBd {
  id: string;
  name: string;
  scope: string;
  status: string;
  profit_tiers: ApiBdTier[];
}
interface ApiBalance {
  client_id: string;
  total_credit: string;
  total_debit: string;
  balance: string;
  gross_topup: string;
}

// --- View rows ---------------------------------------------------------------
interface CrmClient {
  id: string;
  name: string;
  email: string;
  since: string;
  enabled: boolean;
  top_up: string | null;
  net_budget: string | null;
}
interface CrmAgency {
  id: string;
  name: string;
  geos: string[];
  fee: string;
  terms: string;
  role_scope: string;
  status: string;
}
interface CrmBd {
  id: string;
  name: string;
  scope: string;
  status: string;
  share: string;
}

type Tab = "clients" | "agencies" | "bd";
type DetailRecord =
  | { kind: "client"; row: CrmClient }
  | { kind: "agency"; row: CrmAgency }
  | { kind: "bd"; row: CrmBd };

function statusTone(status: string): Tone {
  switch (status) {
    case "active":
      return "credit";
    case "paused":
      return "warn";
    case "disabled":
    case "banned":
      return "danger";
    default:
      return "na";
  }
}

// Summarize a BD partner's profit tiers into a single "share" label (the top
// tier pct), falling back to "—" when no tiers exist.
function tierShare(tiers: ApiBdTier[]): string {
  if (!tiers || tiers.length === 0) return "—";
  const top = tiers[tiers.length - 1];
  const pct = top.pct?.trim();
  return pct ? `${pct}%` : "—";
}

function fmtSince(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

export function CrmPage() {
  const [tab, setTab] = useState<Tab>("clients");
  const [clients, setClients] = useState<CrmClient[]>([]);
  const [agencies, setAgencies] = useState<CrmAgency[]>([]);
  const [bd, setBd] = useState<CrmBd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailRecord | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [c, a, b] = await Promise.all([
          api.get<{ clients: ApiClient[] }>("/admin/clients"),
          api.get<{ agencies: ApiAgency[] }>("/admin/agencies"),
          api.get<{ bd_partners: ApiBd[] }>("/admin/bd"),
        ]);
        if (cancelled) return;

        const baseClients: CrmClient[] = (c.clients ?? []).map((x) => ({
          id: x.id,
          name: x.name,
          email: x.email,
          since: fmtSince(x.created_at),
          enabled: x.enabled,
          top_up: null,
          net_budget: null,
        }));
        setClients(baseClients);
        setAgencies(
          (a.agencies ?? []).map((x) => ({
            id: x.id,
            name: x.name,
            geos: x.geos ?? [],
            fee: x.fee_pct ? `${x.fee_pct}` : "—",
            terms: x.financial_conditions || "—",
            role_scope: x.role_scope || "—",
            status: x.status || "—",
          })),
        );
        setBd(
          (b.bd_partners ?? []).map((x) => ({
            id: x.id,
            name: x.name,
            scope: x.scope || "—",
            status: x.status || "—",
            share: tierShare(x.profit_tiers ?? []),
          })),
        );
        setLoading(false);

        // Enrich client rows with their ledger-derived figures, tolerantly: a
        // failed/slow /balance leaves the row's money as "—" and never blanks
        // the table.
        const balances = await Promise.allSettled(
          baseClients.map((cl) =>
            api.get<ApiBalance>(`/admin/clients/${cl.id}/balance`),
          ),
        );
        if (cancelled) return;
        setClients((prev) =>
          prev.map((cl, i) => {
            const res = balances[i];
            if (res.status !== "fulfilled") return cl;
            return {
              ...cl,
              top_up: res.value.gross_topup,
              net_budget: res.value.balance,
            };
          }),
        );
      } catch (err) {
        if (!cancelled) {
          setError(errMessage(err));
          setLoading(false);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const clientCols: Column<CrmClient>[] = [
    {
      key: "name",
      header: "Client",
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="grid size-6 place-items-center rounded-full bg-nx-elevated text-[0.65rem] font-semibold text-nx-text-2">
            {r.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="leading-tight">
            <div className="font-medium text-nx-text">{r.name}</div>
            <div className="text-[0.7rem] text-nx-text-3">since {r.since}</div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      cell: (r) => <span className="text-nx-text-2">{r.email}</span>,
    },
    {
      key: "topup",
      header: "Top-up",
      align: "right",
      cell: (r) =>
        r.top_up !== null ? (
          <MoneyCell value={r.top_up} tone="credit" />
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "net",
      header: "Balance",
      align: "right",
      cell: (r) =>
        r.net_budget !== null ? (
          <MoneyCell value={r.net_budget} />
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => (
        <StatusPill tone={r.enabled ? "credit" : "danger"} className="capitalize">
          {r.enabled ? "active" : "disabled"}
        </StatusPill>
      ),
    },
  ];

  const agencyCols: Column<CrmAgency>[] = [
    {
      key: "name",
      header: "Agency",
      cell: (r) => <span className="font-medium text-nx-text">{r.name}</span>,
    },
    {
      key: "geos",
      header: "Geos",
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.geos.map((g) => (
            <ToneBadge key={g} tone="na">
              {g}
            </ToneBadge>
          ))}
        </div>
      ),
    },
    {
      key: "fee",
      header: "Fee",
      cell: (r) => <span className="text-nx-text-2">{r.fee}</span>,
    },
    {
      key: "terms",
      header: "Terms",
      cell: (r) => <span className="text-nx-text-2">{r.terms}</span>,
    },
    {
      key: "scope",
      header: "Role scope",
      cell: (r) => (
        <ToneBadge tone="na" className="capitalize">
          {r.role_scope}
        </ToneBadge>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => (
        <StatusPill tone={statusTone(r.status)} className="capitalize">
          {r.status}
        </StatusPill>
      ),
    },
  ];

  const bdCols: Column<CrmBd>[] = [
    {
      key: "name",
      header: "Partner",
      cell: (r) => <span className="font-medium text-nx-text">{r.name}</span>,
    },
    {
      key: "share",
      header: "Top profit share",
      cell: (r) => <ToneBadge tone="profit">{r.share}</ToneBadge>,
    },
    {
      key: "scope",
      header: "Scope",
      cell: (r) => <span className="text-nx-text-2">{r.scope}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => (
        <StatusPill tone={statusTone(r.status)} className="capitalize">
          {r.status}
        </StatusPill>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">CRM</h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            Multi-tenant relationship records — click a row for details.
          </p>
        </div>
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: "clients", label: "Clients" },
            { value: "agencies", label: "Agencies · internal" },
            { value: "bd", label: "BD partners" },
          ]}
        />
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load CRM records</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {tab === "agencies" && (
        <p className="mb-3 rounded-md bg-nx-warn-soft px-3 py-2 text-xs text-nx-warn">
          Agency-facing product deferred — these are admin-internal records, no
          agency login.
        </p>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : tab === "clients" ? (
        <DataTable
          columns={clientCols}
          rows={clients}
          rowKey={(r) => r.id}
          onRowClick={(row) => setDetail({ kind: "client", row })}
          empty="No clients."
        />
      ) : tab === "agencies" ? (
        <DataTable
          columns={agencyCols}
          rows={agencies}
          rowKey={(r) => r.id}
          onRowClick={(row) => setDetail({ kind: "agency", row })}
          empty="No agencies."
        />
      ) : (
        <DataTable
          columns={bdCols}
          rows={bd}
          rowKey={(r) => r.id}
          onRowClick={(row) => setDetail({ kind: "bd", row })}
          empty="No BD partners."
        />
      )}

      <Drawer
        open={detail !== null}
        onOpenChange={(o) => {
          if (!o) setDetail(null);
        }}
        title={detail ? detail.row.name : undefined}
        description={
          detail
            ? detail.kind === "client"
              ? "Client record"
              : detail.kind === "agency"
                ? "Agency · admin-internal record"
                : "BD partner record"
            : undefined
        }
      >
        {detail && <DetailBody record={detail} />}
      </Drawer>
    </AdminShell>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-nx-border py-2 last:border-0">
      <span className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
        {label}
      </span>
      <span className="text-right text-sm text-nx-text">{children}</span>
    </div>
  );
}

function DetailBody({ record }: { record: DetailRecord }) {
  if (record.kind === "client") {
    const r = record.row;
    return (
      <div className="grid">
        <DetailRow label="Email">{r.email}</DetailRow>
        <DetailRow label="Client since">{r.since}</DetailRow>
        <DetailRow label="Total top-up">
          {r.top_up !== null ? (
            <MoneyCell value={r.top_up} tone="credit" />
          ) : (
            "—"
          )}
        </DetailRow>
        <DetailRow label="Balance">
          {r.net_budget !== null ? <MoneyCell value={r.net_budget} /> : "—"}
        </DetailRow>
        <DetailRow label="Status">
          <StatusPill
            tone={r.enabled ? "credit" : "danger"}
            className="capitalize"
          >
            {r.enabled ? "active" : "disabled"}
          </StatusPill>
        </DetailRow>
      </div>
    );
  }
  if (record.kind === "agency") {
    const r = record.row;
    return (
      <div className="grid gap-4">
        <div className="grid">
          <DetailRow label="Geos">{r.geos.join(", ") || "—"}</DetailRow>
          <DetailRow label="Fee">{r.fee}</DetailRow>
          <DetailRow label="Financial terms">{r.terms}</DetailRow>
          <DetailRow label="Role scope">{r.role_scope}</DetailRow>
          <DetailRow label="Status">
            <StatusPill tone={statusTone(r.status)} className="capitalize">
              {r.status}
            </StatusPill>
          </DetailRow>
        </div>
        {/* Wave 9.3: bind the agency's Telegram group to the ops bot. */}
        <div className="grid gap-2">
          <span className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
            Telegram chat
          </span>
          <TelegramChatBinding kind="agency" ownerId={r.id} />
        </div>
      </div>
    );
  }
  const r = record.row;
  return (
    <div className="grid">
      <DetailRow label="Top profit share">{r.share}</DetailRow>
      <DetailRow label="Scope">{r.scope}</DetailRow>
      <DetailRow label="Status">
        <StatusPill tone={statusTone(r.status)} className="capitalize">
          {r.status}
        </StatusPill>
      </DetailRow>
    </div>
  );
}
